import React, { useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';

interface ThemeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThemeSelectionModal: React.FC<ThemeSelectionModalProps> = ({ isOpen, onClose }) => {
  const themeContext = useContext(ThemeContext);

  if (!themeContext) {
    // ThemeContext is undefined, so we can't proceed.
    // This might happen if the component is not rendered within a ThemeProvider.
    // You could throw an error here, or return null, or some fallback UI.
    console.error("ThemeContext not found. Make sure ThemeSelectionModal is rendered within a ThemeProvider.");
    return null;
  }

  const { theme, setTheme } = themeContext;

  if (!isOpen) {
    return null;
  }

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
    onClose(); // Close modal after selection
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center p-4 z-50">
      <div className="bg-white dark:bg-stone-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Select Theme</h2>
          <button
            onClick={onClose}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            aria-label="Close theme selection modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <ul>
          <li>
            <button
              onClick={() => handleThemeChange('system')}
              className={`w-full text-left p-3 rounded-md hover:bg-gray-100 dark:hover:bg-stone-700 ${theme === 'system' ? 'bg-gray-100 dark:bg-stone-700 font-semibold' : ''} text-gray-700 dark:text-gray-200`}
            >
              System Default
            </button>
          </li>
          <li>
            <button
              onClick={() => handleThemeChange('light')}
              className={`w-full text-left p-3 rounded-md hover:bg-gray-100 dark:hover:bg-stone-700 ${theme === 'light' ? 'bg-gray-100 dark:bg-stone-700 font-semibold' : ''} text-gray-700 dark:text-gray-200`}
            >
              Light Theme
            </button>
          </li>
          <li>
            <button
              onClick={() => handleThemeChange('dark')}
              className={`w-full text-left p-3 rounded-md hover:bg-gray-100 dark:hover:bg-stone-700 ${theme === 'dark' ? 'bg-gray-100 dark:bg-stone-700 font-semibold' : ''} text-gray-700 dark:text-gray-200`}
            >
              Dark Theme
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ThemeSelectionModal;
